/**
 * MyTasksPanel — the recruiter's due-today / overdue follow-ups, surfaced at
 * the top of the Leads inbox so reminders live where the work happens.
 *
 * Loads the signed-in user's open tasks (GET /tasks?assignee=&status=) with
 * the embedded contact summary, leads with overdue -> due-today -> upcoming,
 * and completes a task in place (optimistic PATCH, revert on failure). Rows
 * click through to the lead. Collapsible; starts expanded only when something
 * is overdue or due today. Supplemental to the inbox, so it renders nothing
 * while loading, on error, or when there are no open tasks (the quiet-failure
 * convention the inbox already uses for stats and merge candidates).
 *
 * Team view (`showTeamToggle`, Today page only): a Mine/Everyone toggle in the
 * header swaps the panel to the WHOLE team's tasks due that day (today +
 * still-overdue), each row tagged with its assignee. The team view is
 * read-only — a coverage glance, not a place to check off a teammate's work —
 * so the complete button is hidden there. With the toggle present the header
 * always renders (even when your own list is empty) so the option stays
 * reachable. The Leads inbox omits the prop and keeps the personal, quiet-when-
 * empty behavior unchanged.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, Flag, ListTodo, Loader2, User, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { Task, contactName } from '../types';
import { TASK_OPEN_STATUSES, dueBucket, dueChipCls, dueLabel, priorityBadge, useTeam } from '../lib';
import SegmentedControl from './filters/SegmentedControl';

const COLLAPSED_PREVIEW = 6;

const BUCKET_ORDER = { overdue: 0, today: 1, upcoming: 2, none: 3 } as const;

type TaskScope = 'mine' | 'everyone';

const SCOPE_OPTIONS = [
  { value: 'mine', label: 'Mine', icon: <User className="h-3.5 w-3.5" />, title: 'My tasks' },
  { value: 'everyone', label: 'Everyone', icon: <Users className="h-3.5 w-3.5" />, title: "Everyone's tasks due today" },
];

/** Overdue first, then due today, then by due time; dateless tasks last. */
function byUrgency(a: Task, b: Task): number {
  const d = BUCKET_ORDER[dueBucket(a.due_at)] - BUCKET_ORDER[dueBucket(b.due_at)];
  if (d !== 0) return d;
  return (a.due_at || '9999').localeCompare(b.due_at || '9999') || b.created_at.localeCompare(a.created_at);
}

interface MyTasksPanelProps {
  /**
   * Today page only: render a Mine/Everyone toggle that surfaces the whole
   * team's tasks due today (+ overdue), and keep the panel header visible even
   * when the signed-in user has no open tasks (so the toggle stays reachable).
   * Omitted on the Leads inbox, which stays personal and quiet-when-empty.
   */
  showTeamToggle?: boolean;
}

export default function MyTasksPanel({ showTeamToggle = false }: MyTasksPanelProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { byId } = useTeam();

  // `scope` is the live toggle selection (drives the SegmentedControl highlight
  // + which fetch runs). `loadedScope` is the scope the current `tasks` were
  // fetched under — ALL data-dependent rendering reads this, never the live
  // scope. The two converge once a fetch resolves; keeping them separate means a
  // slow response can't repaint the panel under a scope it doesn't belong to
  // (which would, e.g., expose complete buttons on teammates' tasks in the
  // read-only team view), and the title/counts never lead the data.
  const [scope, setScope] = useState<TaskScope>('mine');
  const [loadedScope, setLoadedScope] = useState<TaskScope>('mine');
  const [tasks, setTasks] = useState<Task[] | null>(null); // null = loading/failed
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  // Monotonic request id: only the most recent fetch may commit its result, so a
  // stale in-flight response (after a quick re-toggle) is dropped.
  const reqRef = useRef(0);

  const isTeam = loadedScope === 'everyone';

  const load = useCallback(async () => {
    if (!user?.id) return;
    const team = scope === 'everyone';
    const myReq = ++reqRef.current;
    try {
      const res = await api.get('/api/crm/tasks', {
        // Team view drops the assignee filter (everyone's tasks) and pulls a
        // wider page since it spans the whole team; mine stays scoped + lean.
        params: team
          ? { status: TASK_OPEN_STATUSES, limit: 200 }
          : { assignee: user.id, status: TASK_OPEN_STATUSES, limit: 100 },
      });
      if (myReq !== reqRef.current) return; // a newer load superseded this one
      let list = (res.data?.tasks || []) as Task[];
      // "Everyone's todos due that day" = today + anything still overdue;
      // upcoming and undated tasks are noise for a daily coverage glance.
      if (team) {
        list = list.filter((t) => {
          const b = dueBucket(t.due_at);
          return b === 'overdue' || b === 'today';
        });
      }
      list = list.sort(byUrgency);
      // Commit the list and the scope it belongs to together, so rendering never
      // sees one without the other.
      setTasks(list);
      setLoadedScope(team ? 'everyone' : 'mine');
      // Team view always opens (it's the thing the user just asked to see);
      // mine opens only when something needs attention today.
      setExpanded(team || list.some((t) => dueBucket(t.due_at) !== 'upcoming' && dueBucket(t.due_at) !== 'none'));
    } catch {
      if (myReq !== reqRef.current) return; // stale failure — don't blank fresh data
      setTasks(null); // quiet — the inbox is the primary surface
    }
  }, [user?.id, scope]);

  useEffect(() => { load(); }, [load]);

  const complete = async (task: Task) => {
    if (busyIds.has(task.id)) return;
    const reqAtClick = reqRef.current; // the data context this task belongs to
    setBusyIds((prev) => new Set(prev).add(task.id));
    setTasks((prev) => (prev ? prev.filter((t) => t.id !== task.id) : prev));
    try {
      await api.patch(`/api/crm/tasks/${task.id}`, { status: 'done' });
    } catch {
      // Only restore if the list hasn't been replaced by a newer load meanwhile;
      // otherwise re-inserting would inject this task into a different scope's list.
      setTasks((prev) => (prev && reqRef.current === reqAtClick ? [...prev, task].sort(byUrgency) : prev));
    } finally {
      setBusyIds((prev) => {
        const n = new Set(prev);
        n.delete(task.id);
        return n;
      });
    }
  };

  // Still loading / load failed → stay quiet everywhere.
  if (tasks === null) return null;
  // Leads inbox (no toggle): keep the quiet-when-empty convention. On Today the
  // toggle must stay reachable, so the header renders even with an empty list.
  if (!showTeamToggle && tasks.length === 0) return null;

  const overdue = tasks.filter((t) => dueBucket(t.due_at) === 'overdue').length;
  const dueToday = tasks.filter((t) => dueBucket(t.due_at) === 'today').length;
  const visible = showAll ? tasks : tasks.slice(0, COLLAPSED_PREVIEW);
  const title = isTeam ? 'Team tasks' : 'My tasks';

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-2 px-4 py-3">
        <button
          onClick={() => setExpanded((s) => !s)}
          aria-expanded={expanded}
          className="flex min-h-[44px] min-w-0 flex-1 items-center gap-2 rounded-lg text-left transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-gray-700/50"
        >
          <ListTodo className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
          <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</span>
          <span className="flex-shrink-0 text-sm text-gray-500 dark:text-gray-400">({tasks.length})</span>
          {/* Summary badges are redundant with the per-row due chips; hide them on
              mobile so the title + scope toggle never overflow a narrow header. */}
          {overdue > 0 && (
            <span className="hidden flex-shrink-0 items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 sm:inline-flex dark:bg-red-900/30 dark:text-red-300">
              {overdue} overdue
            </span>
          )}
          {dueToday > 0 && (
            <span className="hidden flex-shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 sm:inline-flex dark:bg-amber-900/40 dark:text-amber-300">
              {dueToday} due today
            </span>
          )}
          <ChevronDown
            className={`ml-auto h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`}
          />
        </button>
        {showTeamToggle && (
          <SegmentedControl
            label="Task scope"
            value={scope}
            onChange={(v) => {
              if (v === scope) return;
              setScope(v as TaskScope);
              setShowAll(false);
            }}
            options={SCOPE_OPTIONS}
          />
        )}
      </div>

      {expanded && (
        <div className="border-t border-gray-100 px-4 pb-3 dark:border-gray-700">
          {tasks.length === 0 ? (
            <p className="py-3 text-sm text-gray-500 dark:text-gray-400">
              {isTeam ? 'No team tasks due today.' : 'No open tasks.'}
            </p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {visible.map((t) => {
                const busy = busyIds.has(t.id);
                const prioCls = priorityBadge(t.priority);
                const hasContact = !!t.contact?.id;
                // Team view is read-only (a coverage glance) so it never shows a
                // complete button — a recruiter shouldn't one-tap a teammate's task.
                const assigneeName = t.assignee_user_id
                  ? byId.get(t.assignee_user_id)?.name || 'Teammate'
                  : 'Unassigned';
                return (
                  <div
                    key={t.id}
                    role={hasContact ? 'button' : undefined}
                    tabIndex={hasContact ? 0 : undefined}
                    onClick={() => hasContact && navigate(`/crm/contacts/${t.contact!.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target === e.currentTarget && hasContact) {
                        navigate(`/crm/contacts/${t.contact!.id}`);
                      }
                    }}
                    className={`flex items-center gap-2.5 py-2 ${
                      hasContact
                        ? 'cursor-pointer transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20'
                        : ''
                    }`}
                  >
                    {!isTeam && (
                      <button
                        onClick={(e) => { e.stopPropagation(); complete(t); }}
                        disabled={busy}
                        title="Mark done"
                        aria-label={`Mark task done: ${t.title}`}
                        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-gray-300 text-transparent transition-colors duration-150 hover:border-primary-600 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600 sm:h-6 sm:w-6"
                      >
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" /> : <Check className="h-3.5 w-3.5" />}
                      </button>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{t.title}</p>
                      {(isTeam || t.contact) && (
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                          {isTeam && <span className="font-medium text-gray-700 dark:text-gray-300">{assigneeName}</span>}
                          {isTeam && t.contact && <span className="text-gray-400"> · </span>}
                          {t.contact && contactName(t.contact)}
                        </p>
                      )}
                    </div>
                    {prioCls && (
                      <span className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${prioCls}`}>
                        <Flag className="h-3 w-3" /> {t.priority}
                      </span>
                    )}
                    {t.due_at && (
                      <span
                        className={`inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${dueChipCls(dueBucket(t.due_at))}`}
                      >
                        {dueLabel(t.due_at)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {tasks.length > COLLAPSED_PREVIEW && (
            <button
              onClick={() => setShowAll((s) => !s)}
              className="mt-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
            >
              {showAll ? 'Show fewer' : `Show all ${tasks.length}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
